import { Button, Select, Spin, Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Checkbox } from '../Checkbox';
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { SELECT_LIMIT } from '@src/constants/pagination';

export type OptionType = {
    label: string;
    value: string;
};

type MultiSelectWithCheckboxesProps = {
    options: OptionType[];
    value?: { value: string; label: string }[];
    size?: 'large' | 'middle' | 'small';
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    hasMore?: boolean;
    allowClear?: boolean;
    fullWidth?: boolean;
    onLoadMore?: () => void;
    onChange: (val: { value: string; label: string }[]) => void;
    onClear?: () => void;
    onSearch?: (val: string) => void;
    className?: string;
    maxLength?: number;
    selectedItemClassName?: string;
};

export const MultiSelectWithCheckboxes = ({
    options,
    value = [],
    size = 'middle',
    placeholder = 'Select options',
    disabled = false,
    loading = false,
    hasMore,
    allowClear,
    fullWidth = false,
    onLoadMore,
    onChange,
    onClear,
    onSearch,
    className,
    maxLength,
    selectedItemClassName,
}: MultiSelectWithCheckboxesProps) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (maxLength) {
            const input = containerRef.current?.querySelector('input');
            if (input) {
                input.setAttribute('maxLength', String(maxLength));
            }
        }
    }, [maxLength]);

    // Reset search when dropdown closes
    useEffect(() => {
        if (!open) {
            // Reset search when closing to ensure clean state on next open
            setSearch('');
            // Also reset parent search to ensure options are restored
            // This is important when search didn't match anything and parent has empty options
            onSearch?.('');
        }
    }, [open]); // Only depend on open, not onSearch to avoid unnecessary resets

    const handleSearch = (val: string) => {
        setSearch(val);
        onSearch?.(val);
    };

    const toggleSelection = (option: OptionType) => {
        const current = value ?? [];
        const isSelected = current.some((v) => v.value === option.value);

        const newSelected = isSelected
            ? current.filter((v) => v.value !== option.value)
            : [...current, { value: option.value, label: option.label }];

        onChange(newSelected);
    };

    const handleRemove = (val: { value: string; label: string }) => {
        onChange(value.filter((v) => v.value !== val.value));
    };

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(search.toLowerCase())
    );

    // If we have fewer options than SELECT_LIMIT, we've likely loaded all available options
    // This prevents showing "Load more" when there are only a few options (e.g., 3)
    const hasLoadedFullPage = options.length >= SELECT_LIMIT;

    // Only show "Load more" if:
    // 1. onLoadMore callback exists
    // 2. hasMore is true (from parent hook)
    // 3. We're not in a search state (search filters locally, so hasMore doesn't apply)
    // 4. There are actually options displayed (to avoid showing when filtered results are empty)
    // 5. We have a full page of options (if we have less than SELECT_LIMIT, we've loaded everything)
    const shouldShowLoadMore =
        onLoadMore &&
        hasMore &&
        !search &&
        filteredOptions.length > 0 &&
        hasLoadedFullPage;

    const handleClear = useCallback(() => {
        setSearch('');
        onClear?.();
    }, [onClear]);

    return (
        <div className="w-full" ref={containerRef}>
            <Select
                mode="multiple"
                size={size}
                placeholder={placeholder}
                value={value?.map((v) => v.value) ?? []}
                onChange={() => {}}
                tagRender={() => <></>}
                disabled={disabled}
                className={clsx('w-full multi-select-with-clear', className)}
                showSearch
                onSearch={handleSearch}
                searchValue={search}
                allowClear={allowClear}
                onClear={handleClear}
                filterOption={false}
                loading={loading}
                notFoundContent={undefined}
                open={open}
                onDropdownVisibleChange={(isOpen) => setOpen(isOpen)}
                popupMatchSelectWidth={false}
                dropdownRender={(menu) => (
                    <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                        {filteredOptions.length > 0
                            ? filteredOptions.map((option) => (
                                  <div
                                      key={option.value}
                                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center w-full"
                                      onClick={() => toggleSelection(option)}
                                  >
                                      <Checkbox
                                          checked={value?.some(
                                              (v) => v.value === option.value
                                          )}
                                      >
                                          <span className="text-sm">
                                              {option.label}
                                          </span>
                                      </Checkbox>
                                  </div>
                              ))
                            : menu}
                        {shouldShowLoadMore && (
                            <div
                                className="px-4 py-2 text-center"
                                onMouseDown={(e) => e.preventDefault()}
                            >
                                <Button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onLoadMore?.();
                                    }}
                                    disabled={loading || !hasMore}
                                    block
                                >
                                    {loading ? (
                                        <Spin size="small" />
                                    ) : hasMore ? (
                                        'Load more'
                                    ) : (
                                        'No more data'
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            />
            {value?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {value.map((val) => (
                        <div
                            key={val.value}
                            className={clsx(
                                `flex items-center px-2 py-1 rounded-md  ${
                                    fullWidth ? 'w-full' : ''
                                }`,
                                selectedItemClassName ??
                                    'bg-gray-200 text-gray-800'
                            )}
                        >
                            <Tooltip
                                title={val.label}
                                className="hidden sm:block"
                            >
                                <span className="truncate flex-grow min-w-0">
                                    {val.label}
                                </span>
                            </Tooltip>
                            <span className="flex-grow min-w-0 block sm:hidden">
                                {val.label}
                            </span>
                            {!disabled && (
                                <button
                                    onClick={() => handleRemove(val)}
                                    className="ml-2 text-gray-500 hover:text-gray-700"
                                >
                                    <CloseOutlined />
                                </button>
                            )}
                        </div>
                    ))}
                    {value?.length > 1 && (
                        <button
                            type="button"
                            aria-label={t('buttons.clearAll')}
                            onClick={() => {
                                onChange([]);
                                onClear?.();
                            }}
                            className="bg-errorContainer gap-2 text-on-background text-R-16 rounded-2xl px-2 py-1 hover:opacity-80 flex items-center"
                        >
                            {t('buttons.clearAll')}
                            <CloseOutlined />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
